import cx from 'classnames';
import { useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { Button, ButtonVariants } from '@components/button/Button';
import { ControlledFormPhoneInput } from '@components/formInputs/ControlledFormPhoneInput';
import { getBrowserPhoneCountry } from '@shared/utils/phoneCountry';
import { ApiError } from '@shared/api/errors';
import { reportAppError } from '@shared/reportAppError';
import { TERMS_OF_SERVICE_PATH } from 'webReservations/paths';
import typography from '~styles/typography.scss';
import {
  AuthModalTypes,
  useAuthModalContext,
} from '../context/AuthModalContext';
import { sendLoginVerificationCode } from './apiHelpers';
import { AuthModal } from './AuthModal';
import styles from './SignInModal.scss';

const noOp = () => undefined;

interface SignInModalProps {
  hideCreateAccount?: boolean;
  nonClosable?: boolean;
  onAuthSuccess?: () => void;
}

interface SignInFormData {
  phone: string;
}

export const SignInModal = ({
  hideCreateAccount = false,
  nonClosable = false,
  onAuthSuccess = noOp,
}: SignInModalProps) => {
  const { handleSubmit, control, getValues } = useForm<SignInFormData>();
  const { openModal, nextModal, closeModal } = useAuthModalContext();
  const [loading, setLoading] = useState<boolean>(false);
  const [sendOTPError, setSendOTPError] = useState<string>('');

  const submitPhoneNumber: SubmitHandler<SignInFormData> = async (
    data,
    e: any,
  ) => {
    const isPotentialBadActor =
      window.navigator.webdriver && !process.env.OPTIMIZED_FOR_E2E;
    if (!e?.isTrusted || isPotentialBadActor) {
      return;
    }

    const { phone } = data;

    setLoading(true);
    try {
      await sendLoginVerificationCode(phone);
      nextModal(AuthModalTypes.SIGN_IN_OTP, {
        hideCreateAccount,
        nonClosable,
        onAuthSuccess,
        phone,
      });
    } catch (error) {
      if (error instanceof ApiError) {
        setSendOTPError(error.message);
      } else {
        setSendOTPError('An error occurred. Please try again.');
      }
      reportAppError(error);
    }

    setLoading(false);
  };

  return (
    <AuthModal
      label="Sign In"
      nonClosable={nonClosable}
      submitHandler={handleSubmit(submitPhoneNumber)}
      title="Please Log In to Continue"
    >
      <div className={styles.container}>
        <ControlledFormPhoneInput
          autoFocus
          control={control}
          customError={sendOTPError}
          defaultCountry={getBrowserPhoneCountry()}
          detectCountryCodeWithoutPlus
          label="Phone"
          name="phone"
          rules={{ required: true }}
          showCountryCodeHint
        />
        <p className={typography.c3}>
          By continuing to use Peak services, you are agreeing to these updated{' '}
          <Link
            className={styles.legalLink}
            onClick={closeModal}
            to={TERMS_OF_SERVICE_PATH}
          >
            Terms of Service
          </Link>
          .
        </p>
        <Button
          isDisabled={loading || !!sendOTPError}
          label="Login"
          type="submit"
          variant={ButtonVariants.Primary}
        />
        {hideCreateAccount ? (
          <p className={cx(typography.c3, styles.noAccountText)}>
            Don't have a Peak account? <br />
            Book a reservation at one of our partner restaurants to get started.
          </p>
        ) : (
          <>
            <p className={typography.c3}>Don't have a Peak account?</p>
            <Button
              label="Create Account"
              onClick={() =>
                openModal(AuthModalTypes.SIGN_UP, {
                  onAuthSuccess,
                  phone: getValues('phone'),
                })
              }
              variant={ButtonVariants.Tertiary}
            />
          </>
        )}
      </div>
    </AuthModal>
  );
};
